import type { CalculationResult, NetBalance, PlayerStackEntry, Transfer, ValidationError } from "@/lib/poker-types";

function toCents(value: number): number {
  return Math.round((value + Number.EPSILON) * 100);
}

function fromCents(cents: number): number {
  return cents / 100;
}

function buildValidationErrors(players: PlayerStackEntry[], totalCashInCents: number, totalFinalChips: number): ValidationError[] {
  const errors: ValidationError[] = [];

  if (players.length < 2) {
    errors.push({
      code: "MIN_PLAYERS",
      message: "Add at least 2 players to calculate a settlement.",
    });
  }

  for (const player of players) {
    if (!player.name.trim()) {
      errors.push({
        code: "NAME_REQUIRED",
        message: "Every player must have a name.",
      });
      break;
    }

    if (!Number.isFinite(player.cashIn) || !Number.isFinite(player.chipsBought) || !Number.isFinite(player.finalChips)) {
      errors.push({
        code: "AMOUNT_INVALID",
        message: "Cash-in and chip values must be valid numbers.",
      });
      break;
    }

    if (player.cashIn < 0 || player.chipsBought < 0 || player.finalChips < 0) {
      errors.push({
        code: "AMOUNT_NEGATIVE",
        message: "Cash-in and chip values cannot be negative.",
      });
      break;
    }
  }

  if (totalCashInCents <= 0) {
    errors.push({
      code: "TOTAL_CASH_IN_INVALID",
      message: "Total cash-in must be greater than zero.",
    });
  }

  if (totalFinalChips <= 0) {
    errors.push({
      code: "TOTAL_FINAL_CHIPS_INVALID",
      message: "Total final chips must be greater than zero.",
    });
  }

  return errors;
}

function allocatePayoutCents(players: PlayerStackEntry[], totalCashInCents: number, totalFinalChips: number): number[] {
  const rawShares = players.map((player) => ({
    base: Math.floor((totalCashInCents * player.finalChips) / totalFinalChips),
    remainder: ((totalCashInCents * player.finalChips) / totalFinalChips) % 1,
  }));

  const baseTotal = rawShares.reduce((sum, share) => sum + share.base, 0);
  let remaining = totalCashInCents - baseTotal;

  const rankedIndexes = rawShares
    .map((share, index) => ({ index, remainder: share.remainder }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);

  const payouts = rawShares.map((share) => share.base);
  let cursor = 0;

  while (remaining > 0 && rankedIndexes.length > 0) {
    payouts[rankedIndexes[cursor % rankedIndexes.length].index] += 1;
    remaining -= 1;
    cursor += 1;
  }

  return payouts;
}

export function calculateSettlement(players: PlayerStackEntry[]): CalculationResult {
  const totalCashInCents = players.reduce((sum, player) => sum + toCents(player.cashIn), 0);
  const totalBoughtChips = players.reduce((sum, player) => sum + player.chipsBought, 0);
  const totalFinalChips = players.reduce((sum, player) => sum + player.finalChips, 0);
  const errors = buildValidationErrors(players, totalCashInCents, totalFinalChips);
  const warnings: string[] = [];
  const chipDifference = totalFinalChips - totalBoughtChips;

  if (chipDifference !== 0) {
    warnings.push(`Final chips are off by ${chipDifference.toFixed(2)} from chips bought.`);
  }

  const payoutCents = totalFinalChips > 0 ? allocatePayoutCents(players, totalCashInCents, totalFinalChips) : [];

  const balancesWithCents = players.map((player, index) => {
    const netCents = (payoutCents[index] ?? 0) - toCents(player.cashIn);

    return {
      id: player.id,
      name: player.name,
      netCents,
    };
  });

  const balances: NetBalance[] = balancesWithCents
    .map((entry) => ({
      id: entry.id,
      name: entry.name,
      amount: fromCents(entry.netCents),
    }))
    .sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name));

  if (errors.length > 0) {
    return {
      balances,
      transfers: [],
      totalCashIn: fromCents(totalCashInCents),
      totalBoughtChips,
      totalFinalChips,
      chipValue: 0,
      chipDifference,
      totalTransferred: 0,
      adjustmentCents: 0,
      warnings,
      errors,
    };
  }

  const creditors = balancesWithCents
    .filter((entry) => entry.netCents > 0)
    .map((entry) => ({ ...entry, remainingCents: entry.netCents }))
    .sort((a, b) => b.remainingCents - a.remainingCents || a.name.localeCompare(b.name));

  const debtors = balancesWithCents
    .filter((entry) => entry.netCents < 0)
    .map((entry) => ({ ...entry, remainingCents: -entry.netCents }))
    .sort((a, b) => b.remainingCents - a.remainingCents || a.name.localeCompare(b.name));

  const transfers: Transfer[] = [];
  let debtorIndex = 0;
  let creditorIndex = 0;

  while (debtorIndex < debtors.length && creditorIndex < creditors.length) {
    const debtor = debtors[debtorIndex];
    const creditor = creditors[creditorIndex];
    const amountCents = Math.min(debtor.remainingCents, creditor.remainingCents);

    if (amountCents > 0) {
      transfers.push({
        fromId: debtor.id,
        fromName: debtor.name,
        toId: creditor.id,
        toName: creditor.name,
        amount: fromCents(amountCents),
      });
    }

    debtor.remainingCents -= amountCents;
    creditor.remainingCents -= amountCents;

    if (debtor.remainingCents === 0) {
      debtorIndex += 1;
    }

    if (creditor.remainingCents === 0) {
      creditorIndex += 1;
    }
  }

  const totalCreditorCents = creditors.reduce((sum, item) => sum + item.netCents, 0);
  const totalTransferCents = transfers.reduce((sum, transfer) => sum + toCents(transfer.amount), 0);
  const adjustmentCents = totalCreditorCents - totalTransferCents;

  if (adjustmentCents !== 0 && transfers.length > 0) {
    const lastTransfer = transfers[transfers.length - 1];
    lastTransfer.amount = fromCents(toCents(lastTransfer.amount) + adjustmentCents);
  }

  return {
    balances,
    transfers,
    totalCashIn: fromCents(totalCashInCents),
    totalBoughtChips,
    totalFinalChips,
    chipValue: totalCashInCents / 100 / totalFinalChips,
    chipDifference,
    totalTransferred: fromCents(totalTransferCents + adjustmentCents),
    adjustmentCents,
    warnings,
    errors: [],
  };
}
